import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useRef } from "react";

export type FocusFact = { key: string; label: string };
export type FocusChrome = {
  size: string;
  facts: readonly FocusFact[];
  source?: { label: string; open: () => void };
  choice?: { view: "preview" | "source"; change: (view: "preview" | "source") => void };
};
export type FocusRegistration = { for: string; chrome: FocusChrome };
export type FocusChannel = {
  card: string;
  put: (registration: FocusRegistration) => void;
  clear: (card: string) => void;
};

const FocusChromeContext = createContext<FocusChannel | null>(null);

export function FocusChromeProvider({ value, children }: { value: FocusChannel; children: ReactNode }) {
  return <FocusChromeContext.Provider value={value}>{children}</FocusChromeContext.Provider>;
}

export function useFocusChrome(chrome: FocusChrome): void {
  const channel = useContext(FocusChromeContext);
  const latest = useRef(chrome);
  latest.current = chrome;
  const shape = `${chrome.source?.label ?? ""}|${chrome.size}|${chrome.facts.map((fact) => `${fact.key}:${fact.label}`).join("|")}|${chrome.choice?.view ?? ""}`;
  const held = useRef<{ shape: string; chrome: FocusChrome }>({ shape: "", chrome });
  if (held.current.shape !== shape) {
    held.current = {
      shape,
      chrome: {
        ...chrome,
        source: chrome.source ? { ...chrome.source, open: () => latest.current.source?.open() } : undefined,
        choice: chrome.choice
          ? { ...chrome.choice, change: (view) => latest.current.choice?.change(view) }
          : undefined,
      },
    };
  }
  const stable = held.current.chrome;
  useEffect(() => {
    if (!channel) return;
    channel.put({ for: channel.card, chrome: stable });
    return () => channel.clear(channel.card);
  }, [channel, stable]);
}
